/* BEATSEEK — per-card save actions for similar-track cards.
   Keepers go where they belong: favourite, wishlist, a crate, Spotify or Discord
   (the last two only when that account is connected). */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Heart, Package, Music, Share2, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import WishlistStar from './WishlistStar';

const API = process.env.REACT_APP_BACKEND_URL;
const token = () => localStorage.getItem('beatseek_token') || '';

const btn = (active) => ({
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 20, height: 20, padding: 0, borderRadius: 5, cursor: 'pointer',
  background: active ? 'rgba(0,229,255,0.14)' : 'rgba(255,255,255,0.05)',
  border: `1px solid ${active ? 'rgba(0,229,255,0.45)' : 'rgba(255,255,255,0.1)'}`,
  color: 'rgba(255,255,255,0.65)', transition: 'background-color 0.18s, border-color 0.18s, color 0.18s',
});

export const RailTrackActions = ({ track, idx, onSaveToSpotify, onShareToDiscord, zIndex = 3000 }) => {
  const [fav, setFav] = useState(false);
  const [busy, setBusy] = useState(false);
  const [crateOpen, setCrateOpen] = useState(false);
  const [crates, setCrates] = useState(null);
  const [savedTo, setSavedTo] = useState(null);
  const [pickerPos, setPickerPos] = useState(null);
  const wrapRef = useRef(null);
  const pickerRef = useRef(null);

  const spotifyOn = !!localStorage.getItem('spotify_access_token');
  const discordOn = !!localStorage.getItem('discord_access_token');

  const trackId = `${track.artist || ''}-${track.title || ''}`.toLowerCase().replace(/\s+/g, '-');
  const payload = {
    id: trackId, title: track.title || '', artist: track.artist || '',
    artwork_url: track.artwork || null, preview_url: track.preview_url || null,
    bpm: track.bpm || null, key: track.key || null, source: 'BEATSEEK Radio',
  };

  useEffect(() => {
    if (!crateOpen) return;
    const close = (e) => {
      const inWrap = wrapRef.current && wrapRef.current.contains(e.target);
      const inPicker = pickerRef.current && pickerRef.current.contains(e.target);
      if (!inWrap && !inPicker) setCrateOpen(false);
    };
    const openedAt = Date.now();
    const bail = () => { if (Date.now() - openedAt > 400) setCrateOpen(false); };
    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', bail, true);
    window.addEventListener('resize', bail);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', bail, true);
      window.removeEventListener('resize', bail);
    };
  }, [crateOpen]);

  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };

  // Is this already a favourite for this user?
  useEffect(() => {
    let dead = false;
    fetch(`${API}/api/users/favorites`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (dead || !d) return;
        const list = d.favorites || [];
        setFav(list.some(f => (f.track_id || f.id) === trackId
          || ((f.title || '').toLowerCase() === (track.title || '').toLowerCase()
              && (f.artist || '').toLowerCase() === (track.artist || '').toLowerCase())));
      })
      .catch(() => {});
    return () => { dead = true; };
    // eslint-disable-next-line
  }, [trackId]);

  const toggleFav = async (e) => {
    stop(e);
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/users/favorites${fav ? `/${encodeURIComponent(trackId)}` : ''}`, {
        method: fav ? 'DELETE' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: fav ? undefined : JSON.stringify({ track: payload }),
      });
      if (!res.ok) throw new Error('Could not save to favourites');
      setFav(!fav);
      toast.success(fav ? 'Removed from favourites' : `Favourited "${track.title}"`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const openCrates = async (e) => {
    stop(e);
    const r = e.currentTarget.getBoundingClientRect();
    setPickerPos({ left: Math.min(Math.max(r.left + r.width / 2 - 86, 8), window.innerWidth - 180), bottom: window.innerHeight - r.top + 6 });
    setCrateOpen(o => !o);
    if (crates) return;
    try {
      const res = await fetch(`${API}/api/crates`, { headers: { Authorization: `Bearer ${token()}` } });
      const data = await res.json();
      setCrates(Array.isArray(data) ? data : (data.crates || []));
    } catch {
      setCrates([]);
    }
  };

  const addToCrate = async (e, crate) => {
    stop(e);
    try {
      const res = await fetch(`${API}/api/crates/${crate.id}/tracks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({
          track_id: trackId, source: 'beatseek_radio', title: payload.title, artist: payload.artist,
          artwork_url: payload.artwork_url, preview_url: payload.preview_url, bpm: payload.bpm,
        }),
      });
      if (!res.ok) throw new Error('Could not add to that crate');
      setSavedTo(crate.name);
      toast.success(`Added to "${crate.name}"`);
      setTimeout(() => { setCrateOpen(false); setSavedTo(null); }, 1100);
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', display: 'flex', gap: 4, justifyContent: 'center', marginTop: 1 }}>
      <button type="button" onClick={toggleFav} style={btn(fav)} title={fav ? 'Remove from favourites' : 'Favourite'} data-testid={`rail-fav-${idx}`}>
        {busy ? <Loader2 size={11} className="animate-spin" /> : <Heart size={11} fill={fav ? '#ff6b9d' : 'none'} color={fav ? '#ff6b9d' : 'currentColor'} />}
      </button>

      <span onClick={stop} style={{ ...btn(false), color: '#facc15' }} title="Wishlist" data-testid={`rail-wishlist-${idx}`}>
        <WishlistStar itemType="track" itemData={payload} size={11} />
      </span>

      <button type="button" onClick={openCrates} style={btn(crateOpen)} title="Add to a crate" data-testid={`rail-crate-${idx}`}>
        <Package size={11} />
      </button>

      {spotifyOn && (
        <button type="button" onClick={(e) => { stop(e); onSaveToSpotify && onSaveToSpotify(track); }} style={{ ...btn(false), color: '#1db954' }} title="Save to a Spotify playlist" data-testid={`rail-spotify-${idx}`}>
          <Music size={11} />
        </button>
      )}
      {discordOn && (
        <button type="button" onClick={(e) => { stop(e); onShareToDiscord && onShareToDiscord(track); }} style={{ ...btn(false), color: '#8f9bf7' }} title="Share to Discord" data-testid={`rail-discord-${idx}`}>
          <Share2 size={11} />
        </button>
      )}

      {crateOpen && createPortal((
        <div
          ref={pickerRef}
          onClick={stop}
          data-testid={`rail-crate-picker-${idx}`}
          style={{
            position: 'fixed', left: pickerPos ? pickerPos.left : 0, bottom: pickerPos ? pickerPos.bottom : 0, zIndex,
            width: 172, maxHeight: 190, overflowY: 'auto', padding: 6, borderRadius: 9,
            background: 'rgba(12,12,20,0.98)', border: '1px solid rgba(0,229,255,0.25)',
            boxShadow: '0 10px 28px rgba(0,0,0,0.6)', textAlign: 'left',
          }}
        >
          {savedTo ? (
            <div style={{ fontSize: 10, color: '#00ff88', display: 'flex', alignItems: 'center', gap: 4, padding: '4px 2px' }}>
              <Check size={11} /> Added to {savedTo}
            </div>
          ) : crates === null ? (
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', padding: '4px 2px' }}>Loading crates…</div>
          ) : crates.length === 0 ? (
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', padding: '4px 2px' }}>No crates yet — make one in Crate Box</div>
          ) : (
            crates.map(c => (
              <button
                key={c.id}
                type="button"
                onClick={(e) => addToCrate(e, c)}
                data-testid={`rail-crate-option-${c.id}`}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', padding: '5px 6px', marginBottom: 2,
                  background: 'none', border: 'none', borderRadius: 6, cursor: 'pointer',
                  color: 'rgba(255,255,255,0.8)', fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(0,229,255,0.1)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
              >
                {c.name}
              </button>
            ))
          )}
        </div>
      ), document.body)}
    </div>
  );
};

export default RailTrackActions;
